import type React from "react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import type { ScanpathData } from "../../types/gazeHeatmapTypes";

/**
 * Small info icon with tooltip on hover
 */
const InfoIcon: React.FC<{ tooltip: string }> = ({ tooltip }) => {
  const [showTooltip, setShowTooltip] = useState(false);

  return (
    <div className="relative inline-block">
      <button
        type="button"
        className="text-gray-400 hover:text-gray-600 transition-colors focus:outline-none"
        onMouseEnter={() => setShowTooltip(true)}
        onMouseLeave={() => setShowTooltip(false)}
        onFocus={() => setShowTooltip(true)}
        onBlur={() => setShowTooltip(false)}
        aria-label="Information"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="h-3 w-3"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
      </button>
      {showTooltip && (
        <div className="absolute z-50 bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-2 text-xs text-white bg-gray-800 rounded-lg shadow-lg w-48 text-left">
          {tooltip}
          <div className="absolute top-full left-1/2 -translate-x-1/2 -mt-1">
            <div className="w-2 h-2 bg-gray-800 rotate-45" />
          </div>
        </div>
      )}
    </div>
  );
};

interface ScanpathLegendProps {
  /** Scanpath data with statistics */
  scanpath: ScanpathData | null;
  /** Whether scanpath is being generated */
  isLoading?: boolean;
  /** Additional CSS classes */
  className?: string;
}

/**
 * Legend component displaying scanpath statistics and visual guide
 */
const ScanpathLegend: React.FC<ScanpathLegendProps> = ({
  scanpath,
  isLoading = false,
  className = "",
}) => {
  const { t } = useTranslation();

  if (isLoading) {
    return (
      <div className={`flex items-center gap-2 text-sm text-gray-500 ${className}`}>
        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-green-600" />
        {t("statsNew.scanpath.analyzing")}
      </div>
    );
  }

  if (!scanpath) {
    return null;
  }

  const formatDuration = (ms: number): string => {
    if (ms < 1000) return `${Math.round(ms)}ms`;
    return `${(ms / 1000).toFixed(1)}s`;
  };

  const formatAngle = (degrees: number): string => {
    return `${degrees.toFixed(1)}°`;
  };

  const formatPercentage = (ratio: number): string => {
    return `${(ratio * 100).toFixed(0)}%`;
  };

  return (
    <div className={`flex items-center gap-4 text-sm flex-wrap ${className}`}>
      {/* Visual legend */}
      <div className="flex items-center gap-3">
        <div className="flex items-center gap-1.5">
          <div className="w-4 h-4 rounded-full bg-blue-500/60 border-2 border-blue-500 flex items-center justify-center">
            <span className="text-white text-[8px] font-bold">1</span>
          </div>
          <span className="text-gray-500">{t("statsNew.scanpath.fixation")}</span>
          <InfoIcon tooltip={t("statsNew.scanpath.tooltipFixation")} />
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-6 h-0.5 bg-gray-500" />
          <span className="text-gray-500">{t("statsNew.scanpath.saccade")}</span>
          <InfoIcon tooltip={t("statsNew.scanpath.tooltipSaccade")} />
        </div>
      </div>

      <div className="h-4 w-px bg-gray-300" />

      {/* Statistics */}
      <div className="flex items-center gap-3 text-gray-600">
        <span>
          <span className="font-medium">{scanpath.fixationCount}</span>{" "}
          {t("statsNew.scanpath.fixations")}
        </span>
        <span className="flex items-center gap-1">
          {t("statsNew.scanpath.avgDuration")}:{" "}
          <span className="font-medium">{formatDuration(scanpath.averageFixationDuration)}</span>
          <InfoIcon tooltip={t("statsNew.scanpath.tooltipAvgDuration")} />
        </span>
        <span className="flex items-center gap-1">
          {t("statsNew.scanpath.avgAmplitude")}:{" "}
          <span className="font-medium">{formatAngle(scanpath.averageSaccadeAmplitude)}</span>
          <InfoIcon tooltip={t("statsNew.scanpath.tooltipAvgAmplitude")} />
        </span>
        <span className="flex items-center gap-1">
          {t("statsNew.scanpath.fixationRatio")}:{" "}
          <span className="font-medium">{formatPercentage(scanpath.fixationRatio)}</span>
          <InfoIcon tooltip={t("statsNew.scanpath.tooltipFixationRatio")} />
        </span>
      </div>

      {/* Processing time (dev only) */}
      {process.env.NODE_ENV === "development" && (
        <span className="text-xs text-gray-400">({scanpath.processingTime.toFixed(1)}ms)</span>
      )}
    </div>
  );
};

export default ScanpathLegend;
